import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, CalendarClock, GraduationCap, Save } from "lucide-react";
import dayjs from "dayjs";
import { Select } from "antd";

import { ContainerCard } from "../../components/ui/cards";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/input";
import { cn } from "../../utils/tailwindUtils";
import ToastMsg from "../../components/common/ToastMsg";
import useApiMethods from "../../lib/useApiMethods";
import { useTrainingPermissions } from "../../hooks/useTrainingPermissions";
import {
    TRAINING_TASKS,
    TRAINING_MY_TASKS,
    TRAINING_MY_TASK_STATUS,
    TRAINING_ASSIGNMENT,
} from "../../constants/api";

const statusStyles = {
    pending: "bg-gray-100 text-gray-700 dark:bg-gray-700/40 dark:text-gray-200",
    in_progress: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
    completed: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300",
    overdue: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300",
};
const statusLabels = {
    pending: "Pending",
    in_progress: "In Progress",
    completed: "Completed",
    overdue: "Overdue",
};
const priorityStyles = {
    low: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200",
    medium: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300",
    high: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300",
};

const HeaderCard = ({ title, priority, due_date, due_time }) => (
    <div className="bg-card border border-border rounded-lg p-4 mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
            <GraduationCap className="h-6 w-6 text-sidebar-primary flex-shrink-0" />
            <div className="min-w-0">
                <h2 className="text-lg font-semibold truncate">{title}</h2>
                <div className="flex items-center gap-2 mt-1 text-sm text-muted-foreground">
                    <CalendarClock className="h-4 w-4" />
                    <span>Due {due_date ? dayjs(due_date).format("DD-MM-YYYY") : "-"}{due_time ? ` ${due_time}` : ""}</span>
                </div>
            </div>
        </div>
        <span className={cn("px-3 py-1 text-xs font-medium rounded-full capitalize", priorityStyles[priority] || priorityStyles.medium)}>
            {priority} priority
        </span>
    </div>
);

const TrainingDetail = ({ mode = "assignment" }) => {
    const navigate = useNavigate();
    const { id } = useParams();
    const { fetch, update } = useApiMethods();

    const { canAssignTraining } = useTrainingPermissions();
    const myEmployeeId = sessionStorage.getItem("employee_id");

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [task, setTask] = useState(null);
    const [assignment, setAssignment] = useState(null);
    const [employeeStatus, setEmployeeStatus] = useState("");
    const [employeeRemarks, setEmployeeRemarks] = useState("");
    const [adminEdits, setAdminEdits] = useState({});

    const loadAssignment = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetch(TRAINING_MY_TASKS, { id });
            const data = res?.data;
            if (data) {
                setAssignment(data);
                setEmployeeStatus(data.status === "overdue" ? "in_progress" : data.status);
                setEmployeeRemarks(data.employee_remarks || "");
            }
        } catch (_) {
            ToastMsg("error", "Failed to load training");
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const loadTask = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetch(TRAINING_TASKS, { id });
            if (res?.data) setTask(res.data);
        } catch (_) {
            ToastMsg("error", "Failed to load training");
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    useEffect(() => {
        if (mode === "task" && !canAssignTraining) {
            navigate("/training", { replace: true });
            return;
        }
        if (mode === "task") loadTask();
        else loadAssignment();
    }, [mode, loadTask, loadAssignment, canAssignTraining, navigate]);

    const handleEmployeeSave = async () => {
        if (!employeeStatus) {
            ToastMsg("error", "Please choose a status");
            return;
        }
        setSaving(true);
        try {
            const res = await update(TRAINING_MY_TASK_STATUS, {
                id: assignment.id,
                status: employeeStatus,
                remarks: employeeRemarks,
            });
            if (res?.status === "success") {
                ToastMsg("success", res.message || "Status updated");
                loadAssignment();
            } else {
                ToastMsg("error", res?.message || "Failed to update");
            }
        } catch (_) {
            ToastMsg("error", "Failed to update");
        } finally {
            setSaving(false);
        }
    };

    const handleAdminOverride = async (assignmentId) => {
        const payload = { id: assignmentId, ...adminEdits[assignmentId] };
        setSaving(true);
        try {
            const res = await update(TRAINING_ASSIGNMENT, payload);
            if (res?.status === "success") {
                ToastMsg("success", res.message || "Assignment updated");
                setAdminEdits((prev) => ({ ...prev, [assignmentId]: {} }));
                loadTask();
            } else {
                ToastMsg("error", res?.message || "Failed to update");
            }
        } catch (_) {
            ToastMsg("error", "Failed to update");
        } finally {
            setSaving(false);
        }
    };

    if (loading && !assignment && !task) {
        return <ContainerCard scrollable><div className="p-6 text-muted-foreground">Loading...</div></ContainerCard>;
    }

    return (
        <ContainerCard scrollable>
            <div className="p-3">
                <Button size="sm" variant="ghost" onClick={() => navigate(-1)} className="mb-3">
                    <ArrowLeft className="h-4 w-4 mr-1" /> Back
                </Button>

                {mode === "assignment" && assignment && (
                    <>
                        <HeaderCard title={assignment.title} priority={assignment.priority} due_date={assignment.due_date} due_time={assignment.due_time} />

                        <div className="bg-card border border-border rounded-lg p-4 mb-4">
                            <div className="flex items-center justify-between mb-2">
                                <h3 className="font-semibold">Description</h3>
                                <span className={cn("px-2 py-0.5 text-xs font-medium rounded-full", statusStyles[assignment.effective_status])}>
                                    {statusLabels[assignment.effective_status]}
                                </span>
                            </div>
                            <p className="text-sm text-muted-foreground whitespace-pre-line">
                                {assignment.description || "No description provided."}
                            </p>
                            {assignment.instructions && (
                                <>
                                    <h4 className="font-semibold mt-4 mb-1">Instructions</h4>
                                    <p className="text-sm text-muted-foreground whitespace-pre-line">{assignment.instructions}</p>
                                </>
                            )}
                        </div>

                        {String(assignment.employee_id) === String(myEmployeeId) && (
                        <div className="bg-card border border-border rounded-lg p-4">
                            <h3 className="font-semibold mb-3">Update Your Status</h3>
                            <div className="flex flex-col sm:flex-row gap-3">
                                <Select
                                    size="large"
                                    value={employeeStatus || undefined}
                                    onChange={setEmployeeStatus}
                                    options={[
                                        { value: "pending", label: "Pending" },
                                        { value: "in_progress", label: "In Progress" },
                                        { value: "completed", label: "Completed" },
                                    ]}
                                    style={{ minWidth: 180 }}
                                    placeholder="Select status"
                                />
                                <Textarea
                                    value={employeeRemarks}
                                    onChange={(e) => setEmployeeRemarks(e.target.value)}
                                    placeholder="Optional remarks..."
                                    rows={2}
                                    className="flex-1"
                                />
                                <Button onClick={handleEmployeeSave} disabled={saving} className="bg-sidebar text-primary-foreground hover:bg-sidebar-primary">
                                    <Save className="h-4 w-4 mr-1" /> Save
                                </Button>
                            </div>
                        </div>
                        )}
                    </>
                )}

                {mode === "task" && task && (
                    <>
                        <HeaderCard title={task.title} priority={task.priority} due_date={task.due_date} due_time={task.due_time} />

                        <div className="bg-card border border-border rounded-lg p-4 mb-4">
                            <h3 className="font-semibold mb-2">Description</h3>
                            <p className="text-sm text-muted-foreground whitespace-pre-line">{task.description || "-"}</p>
                            {task.instructions && (
                                <>
                                    <h4 className="font-semibold mt-4 mb-1">Instructions</h4>
                                    <p className="text-sm text-muted-foreground whitespace-pre-line">{task.instructions}</p>
                                </>
                            )}
                            <div className="mt-3 text-xs text-muted-foreground">
                                Progress: {task.completed_count}/{task.total_assignees} ({task.completion_percent}%)
                            </div>
                        </div>

                        <div className="bg-card border border-border rounded-lg p-4">
                            <div className="flex items-center justify-between mb-3">
                                <h3 className="font-semibold">Assignees</h3>
                                {canAssignTraining && (
                                    <Button size="sm" variant="outline" onClick={() => navigate(`/training/edit/${task.id}`)}>
                                        Edit Training
                                    </Button>
                                )}
                            </div>
                            <div className="max-h-[min(480px,calc(100vh-360px))] overflow-auto">
                                <table className="w-full text-sm">
                                    <thead className="bg-muted/40">
                                        <tr className="text-left text-muted-foreground">
                                            <th className="px-3 py-2 font-medium">Employee</th>
                                            <th className="px-3 py-2 font-medium">Department</th>
                                            <th className="px-3 py-2 font-medium">Status</th>
                                            <th className="px-3 py-2 font-medium">Employee Remarks</th>
                                            {canAssignTraining && <th className="px-3 py-2 font-medium">Admin Override</th>}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(task.assignments || []).map((a) => {
                                            const edit = adminEdits[a.id] || {};
                                            return (
                                                <tr key={a.id} className="border-t border-border align-top">
                                                    <td className="px-3 py-2">{a.employee_name}</td>
                                                    <td className="px-3 py-2">{a.employee_department || "-"}</td>
                                                    <td className="px-3 py-2">
                                                        <span className={cn("px-2 py-0.5 text-xs font-medium rounded-full", statusStyles[a.effective_status])}>
                                                            {statusLabels[a.effective_status]}
                                                        </span>
                                                    </td>
                                                    <td className="px-3 py-2 max-w-xs">
                                                        <p className="text-xs text-muted-foreground line-clamp-2">{a.employee_remarks || "-"}</p>
                                                    </td>
                                                    {canAssignTraining && (
                                                        <td className="px-3 py-2">
                                                            <div className="flex flex-col gap-2">
                                                                <Select
                                                                    size="large"
                                                                    value={edit.status ?? a.status}
                                                                    onChange={(v) => setAdminEdits((p) => ({ ...p, [a.id]: { ...p[a.id], status: v } }))}
                                                                    options={[
                                                                        { value: "pending", label: "Pending" },
                                                                        { value: "in_progress", label: "In Progress" },
                                                                        { value: "completed", label: "Completed" },
                                                                        { value: "overdue", label: "Overdue" },
                                                                    ]}
                                                                    style={{ minWidth: 140 }}
                                                                    placeholder="Select status"
                                                                />
                                                                <Textarea
                                                                    value={edit.admin_remarks ?? (a.admin_remarks || "")}
                                                                    onChange={(e) => setAdminEdits((p) => ({ ...p, [a.id]: { ...p[a.id], admin_remarks: e.target.value } }))}
                                                                    placeholder="Admin remarks..."
                                                                    rows={2}
                                                                    className="text-xs"
                                                                />
                                                                <Button size="sm" onClick={() => handleAdminOverride(a.id)} disabled={saving} className="bg-sidebar text-primary-foreground hover:bg-sidebar-primary">
                                                                    Apply
                                                                </Button>
                                                            </div>
                                                        </td>
                                                    )}
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </ContainerCard>
    );
};

export default TrainingDetail;
